import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';

import { makeStyles } from '@material-ui/core/styles';

import { showSuccessSnackbar } from 'modules/snackbar';
import { aiCategoryProduct } from 'lib/api/goods';
import { fileDownload } from 'lib/commonLib';
import CurrentStatus from 'components/atoms/CurrentStatus';
import NewButton from 'components/molecules/NewButton';
import FunctionWrap from 'components/molecules/FunctionWrap';

const useStyles = makeStyles((theme) => ({
  categoryWrap: { display: 'block' },
  categoryBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    padding: '20px 10px',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    '& > div:first-child': {
      paddingRight: 20,
      borderRight: '1px solid #ebebeb',
    },
  },
  oDepth: {
    minWidth: 140,
    marginRight: 10,
    '& > div': { marginBottom: 5 },
    '& > div:last-child': { marginBottom: 0 },
  },
  rightDepthBox: { '& > div:last-child': { marginBottom: '0' } },
  rtbRightBox: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > div': {
      width: 'auto',
      marginTop: 0,
      padding: 0,
      ' & > div': { borderRight: 0 },
    },
  },
  DepthItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: 10,
  },
  sDepth: {
    minWidth: 140,
    padding: 10,
    borderRight: '1px solid #ebebeb',
    marginRight: 30,
    '& > div': { marginBottom: 5 },
    '& > div:last-child': { marginBottom: 0 },
  },
  tDepth: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > div': { marginRight: 10 },
  },
  tDepthItem: { margin: '5px 10px 5px 0;', minWidth: 100 },
  depthWrap: {
    '& > div > div': { '&:first-child': { marginTop: 10 }, marginBottom: 10 },
  },
  tabSectionArea: { '& > div': { marginBottom: 10 } },
  button: { minWidth: 50, padding: 3 },
  modalInput: { width: 200, '& input': { width: '100%' } },
  btnColorEdit: { color: '#d90' },
  btnColorDelete: { color: '#e0a' },
  isRtbEditWrap: {
    '& > div': { display: 'block', '&:first-child': { marginBottom: 10 } },
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    '& > div:first-child': { marginRight: 10 },
    '& > p': { marginTop: 0, marginBottom: 0 },
  },
}));

const AiCategoryProduct = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { categoryType } = props;
  const typeIsRtb = categoryType === 'openrtb';

  const [tableData, setTableData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, []);

  /**
   * 페이지 리스트 데이터를 호출하는 함수
   */
  const getData = (isExcel = false) => {
    setLoading(true);
    if (isExcel) {
      aiCategoryProduct({ params: { excel: 1 }, responseType: 'blob' })
        .then((response) => {
          fileDownload(response);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      aiCategoryProduct()
        .then((response) => {
          const { data } = response.data;
          setTableData(data);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <FunctionWrap loading={loading}>
        <NewButton type="filter" onClick={() => getData(true)} disableRipple>
          {t('엑셀 다운로드')}
        </NewButton>
      </FunctionWrap>
      {loading ? (
        <CurrentStatus type="loading" option="useMinHeight" />
      ) : (
        <>
          {tableData !== false && typeof tableData.oDepth.data !== 'undefined' && (
            <div className={classes.categoryWrap}>
              {tableData.oDepth.data.map((oRow, oRowIndex) => (
                <div className={classes.categoryBox} key={oRowIndex.toString()}>
                  <div className={classes.oDepth}>
                    <div className={classes.titleWrap}>
                      <p>
                        {oRow.CTGR_NM} ({oRow.CTGR_CNT})
                      </p>
                    </div>
                    <div>
                      [{t('코드')} : {oRow.CODE}]
                    </div>
                  </div>
                  {typeof tableData.sDepth[oRow.CODE] !== 'undefined' && (
                    <div
                      className={clsx(
                        classes.rightDepthBox,
                        typeIsRtb && classes.rtbRightBox
                      )}
                    >
                      {tableData.sDepth[oRow.CODE].data.map(
                        (sRow, sRowIndex) => {
                          return (
                            <div
                              className={classes.DepthItem}
                              key={sRowIndex.toString()}
                            >
                              <div
                                className={classes.sDepth}
                                key={`${sRow.CODE}tableCell`}
                              >
                                <div className={classes.titleWrap}>
                                  <p>{sRow.CTGR_NM} </p>
                                  {!typeIsRtb && <>({sRow.CTGR_CNT})</>}
                                </div>
                                <div>
                                  [{t('코드')} : {sRow.CODE}]
                                </div>
                              </div>
                              {typeof tableData.tDepth[sRow.CODE] !==
                                'undefined' && (
                                <div key={`${sRow.CODE}tableCell2`}>
                                  <div className={classes.tDepth}>
                                    {tableData.tDepth[sRow.CODE].data.map(
                                      (tRow, tRowIndex) => {
                                        return (
                                          <div key={tRowIndex.toString()}>
                                            <div
                                              className={clsx(
                                                classes.tDepthItem,
                                                classes.titleWrap
                                              )}
                                            >
                                              {tRow.CTGR_NM}
                                              <br />[{t('코드')} : {tRow.CODE}]
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AiCategoryProduct;
